import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Card,
  Grid,
} from '@material-ui/core';
import {
  getStoreCategoriesApi,
  getStoreProductsApi,
  getStoreCustomerGroupsApi,
  saveRequestPromotionEmailDataApi
} from 'api';
import {
  showNotification
} from 'redux/actions/ui';
import { Formik } from 'formik';
import { BackdropCircular } from 'components';
import AutomaticPromotionSummary from '../AutomaticPromotionSummary';
import { validationSchema } from '../validation';
import { initialValues } from './helpers';
import useStyles from './styles';
import RequestPromotionTitle from './RequestPromotionTitle';
import RequestPromotionDiscount from './RequestPromotionDiscount';
import RequestPromotionPercentageOff from './RequestPromotionDiscountTypes/RequestPromotionPercentageOff';
import RequestPromotionDollarOff from './RequestPromotionDiscountTypes/RequestPromotionDollarOff';
import RequestPromotionFixedPrice from './RequestPromotionDiscountTypes/RequestPromotionFixedPrice';
import RequestPromotionNthElement from './RequestPromotionDiscountTypes/RequestPromotionNthElement';
import RequestPromotionBuyXGetY from './RequestPromotionDiscountTypes/RequestPromotionBuyXGetY';
import RequestPromotionCustomerEligibility from './RequestPromotionCustomerEligibility';
import RequestPromotionExclusions from './RequestPromotionExclusions';
import RequestPromotionSpecialInstructions from './RequestPromotionSpecialInstructions';
import RequestPromotionUploadFiles from './RequestPromotionUploadFiles';
import RequestPromotionActiveDates from './RequestPromotionActiveDates';


const RequestPromotionForm = ({isFormBasic = false, isFormComplex = false}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { storeActiveCategoriesWithAllActiveParents } = useSelector((state: IReduxState) => state.categories);
  const { store_products: { catalogActiveProducts }} = useSelector((state: IReduxState) => state.products);
  const { customerGroups = [] } = useSelector((state: IReduxState) => state.customers);
  const { saveEmailDataLoading } = useSelector((state: IReduxState) => state.email);

  useEffect(() => {
    if (!storeActiveCategoriesWithAllActiveParents.length) {
      getStoreCategoriesApi({dispatch});
    }

    if (!catalogActiveProducts.length) {
      getStoreProductsApi({dispatch});
    }

    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }
  }, []);

  const handleSubmit = async (emailData, actions) => {
    await saveRequestPromotionEmailDataApi({ dispatch, emailData });
    await actions.resetForm(initialValues);
    history.push('/my-promotions');
  };

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleSubmit,
          setFieldValue,
          handleChange
        }) => (
          <form onSubmit={handleSubmit} noValidate className="max-w-1000 mx-auto">
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <RequestPromotionTitle
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  classes={classes}
                />
                <RequestPromotionDiscount
                  isFormComplex={isFormComplex}
                  isFormBasic={isFormBasic}
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                  classes={classes}
                />
                {values.discountType.value === 'percentageOff' &&
                  <RequestPromotionPercentageOff
                    values={values}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    errors={errors}
                  />
                }
                {values.discountType.value === 'dollarOff' &&
                  <RequestPromotionDollarOff
                    values={values}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    errors={errors}
                  />
                }
                {values.discountType.value === 'fixedPrice' &&
                  <RequestPromotionFixedPrice
                    values={values}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    errors={errors}
                  />
                }
                {values.discountType.value === 'everyNth' &&
                  <RequestPromotionNthElement
                    values={values}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    errors={errors}
                  />
                }
                {!(values.discountType.value === 'buyXGetY' ||
                  values.discountType.value === 'everyNth') &&
                  <RequestPromotionBuyXGetY
                    values={values}
                    setFieldValue={setFieldValue}
                    touched={touched}
                    errors={errors}
                  />
                }
                <RequestPromotionCustomerEligibility
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                  classes={classes}
                />
                <RequestPromotionExclusions
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
                <RequestPromotionSpecialInstructions
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
                <RequestPromotionUploadFiles setFieldValue={setFieldValue} />
                <RequestPromotionActiveDates
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                  classes={classes}
                />
              </Grid>
              <Grid item xs={12} md={4} className="relative">
                <Card className="p-5 mt-4 sticky" elevation={0}>
                  <AutomaticPromotionSummary values={values} />
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  className="my-4"
                  onClick={() => {
                    if (Object.keys(errors).length) {
                      dispatch(showNotification('error', 'Required fields are missing'));
                    }
                  }}
                >
                  Submit Request
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
      <BackdropCircular loading={saveEmailDataLoading} />
    </div>
  );
};

export default RequestPromotionForm;
